import React from 'react';
import { useWindowSize } from '../../hooks';

/**
 * Componente para exibir agendamentos de um dia específico em formato de tabela ou cards
 * Adapta-se automaticamente para diferentes tamanhos de tela
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Array} props.appointments - Lista de agendamentos a serem exibidos
 * @param {string} props.date - Data dos agendamentos em formato amigável (ex: "14/03/2025")
 * @param {Function} props.onEdit - Função para editar um agendamento
 * @param {Function} props.onCancel - Função para cancelar um agendamento
 * @param {Function} props.onDelete - Função para excluir um agendamento
 * @param {Function} props.onConfirm - Função para confirmar um agendamento
 * @param {Function} props.onReschedule - Função para remarcar um agendamento
 * @returns {JSX.Element} - Elemento React
 */
const AppointmentDayTable = ({
  appointments = [],
  date = 'Hoje',
  onEdit,
  onCancel,
  onDelete,
  onConfirm,
  onReschedule
}) => {
  // Determinar se estamos em uma tela de dispositivo móvel
  const { width } = useWindowSize();
  const isMobile = width < 768;

  /**
   * Renderiza o status do agendamento com formatação adequada
   * @param {string} status - Status do agendamento (Confirmado, Pendente, Cancelado)
   * @returns {JSX.Element} - Elemento com classe de cor correspondente
   */
  const renderStatus = (status) => {
    const statusToLower = status?.toLowerCase() || '';
    let colorClass = 'bg-gray-100 text-gray-800';
    
    if (statusToLower === 'confirmado') {
      colorClass = 'bg-green-100 text-green-800';
    } else if (statusToLower === 'pendente') {
      colorClass = 'bg-yellow-100 text-yellow-800';
    } else if (statusToLower === 'cancelado') {
      colorClass = 'bg-red-100 text-red-800';
    }
    
    return (
      <span className={`px-2 py-1 inline-flex text-xs leading-4 font-medium rounded-full ${colorClass}`}>
        {status}
      </span>
    );
  };

  /**
   * Determina a classe de cor de fundo para cada linha ou card
   * @param {string} status - Status do agendamento
   * @returns {string} - Classe CSS para o fundo
   */
  const getRowBackgroundClass = (status) => {
    const statusToLower = status?.toLowerCase() || '';
    let bgClass = 'hover:bg-gray-50';
    
    if (statusToLower === 'confirmado') {
      bgClass += ' bg-green-50/30';
    } else if (statusToLower === 'pendente') {
      bgClass += ' bg-yellow-50/30';
    } else if (statusToLower === 'cancelado') {
      bgClass += ' bg-red-50';
    }
    
    return bgClass;
  };

  // Formatação para visualização em dispositivos móveis - usando cards
  if (isMobile) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
        <h3 className="font-semibold text-gray-700 mb-3">
          Agendamentos de {date} ({appointments.length})
        </h3>
        
        {appointments.length === 0 ? (
          <div className="py-6 text-center text-gray-500">
            Não há agendamentos para esta data.
          </div>
        ) : (
          <div className="space-y-3">
            {appointments.map((appointment, index) => {
              const isPending = appointment.status?.toLowerCase() === 'pendente';
              const isCanceled = appointment.status?.toLowerCase() === 'cancelado';
              
              return (
                <div 
                  key={appointment.id || index} 
                  className={`rounded-lg border border-gray-200 overflow-hidden ${getRowBackgroundClass(appointment.status)}`}
                >
                  <div className="p-3">
                    {/* Cabeçalho com hora e status */}
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-gray-800">{appointment.time || appointment.hora}</span>
                      {renderStatus(appointment.status)}
                    </div>
                    
                    {/* Dados do cliente */}
                    <div className="mb-2">
                      <div className="font-medium">{appointment.client?.name || appointment.clientName}</div>
                      <div className="text-xs text-gray-500">{appointment.client?.phone || appointment.telefone}</div>
                    </div>
                    
                    {/* Informações de serviço e profissional */}
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <div className="text-gray-500 text-xs">Serviço:</div>
                        <div>{appointment.service || appointment.servico}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs">Profissional:</div>
                        <div>{appointment.professional || appointment.profissional}</div>
                      </div>
                    </div>
                    
                    {/* Botões de ação */}
                    <div className="flex flex-wrap gap-2 mt-3 pt-2 border-t border-gray-200">
                      <button 
                        type="button" 
                        className="px-3 py-1 text-xs rounded bg-blue-50 text-blue-600"
                        onClick={() => onEdit && onEdit(appointment)}
                      >
                        Editar
                      </button>
                      
                      {isPending && (
                        <button 
                          type="button" 
                          className="px-3 py-1 text-xs rounded bg-green-50 text-green-600"
                          onClick={() => onConfirm && onConfirm(appointment)}
                        >
                          Confirmar
                        </button>
                      )}
                      
                      {!isCanceled && (
                        <>
                          <button 
                            type="button" 
                            className="px-3 py-1 text-xs rounded bg-red-50 text-red-600"
                            onClick={() => onCancel && onCancel(appointment)}
                          >
                            Cancelar
                          </button>
                          
                          <button 
                            type="button" 
                            className="px-3 py-1 text-xs rounded bg-orange-50 text-orange-600"
                            onClick={() => onReschedule && onReschedule(appointment)}
                          >
                            Remarcar
                          </button>
                        </>
                      )}
                      
                      <button 
                        type="button" 
                        className="px-3 py-1 text-xs rounded bg-gray-50 text-gray-600"
                        onClick={() => onDelete && onDelete(appointment)}
                      >
                        Excluir
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }
  
  // Renderização desktop - usando tabela tradicional
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
      <h3 className="font-semibold text-gray-700 mb-3">
        Agendamentos de {date} ({appointments.length})
      </h3>
      
      {appointments.length === 0 ? (
        <div className="py-6 text-center text-gray-500">
          Não há agendamentos para esta data.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Hora
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Serviço
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Profissional
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            
            <tbody className="divide-y divide-gray-100">
              {appointments.map((appointment, index) => {
                const isPending = appointment.status?.toLowerCase() === 'pendente';
                const isCanceled = appointment.status?.toLowerCase() === 'cancelado';
                
                return (
                  <tr key={appointment.id || index} className={getRowBackgroundClass(appointment.status)}>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      {appointment.time || appointment.hora}
                    </td>
                    
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="text-sm font-medium">
                        {appointment.client?.name || appointment.clientName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {appointment.client?.phone || appointment.telefone}
                      </div>
                    </td>
                    
                    <td className="px-3 py-2">
                      <div className="flex flex-col space-y-1">
                        <span className="text-xs">{appointment.service || appointment.servico}</span>
                      </div>
                    </td>
                    
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      {appointment.professional || appointment.profissional}
                    </td>
                    
                    <td className="px-3 py-2 whitespace-nowrap">
                      {renderStatus(appointment.status)}
                    </td>
                    
                    <td className="px-3 py-2 whitespace-nowrap text-right text-sm space-x-1">
                      <button 
                        type="button" 
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                        onClick={() => onEdit && onEdit(appointment)}
                      >
                        Editar
                      </button>
                      
                      <span className="text-gray-300">|</span>
                      
                      {isPending && (
                        <>
                          <button 
                            type="button" 
                            className="text-green-600 hover:text-green-800 font-medium text-sm"
                            onClick={() => onConfirm && onConfirm(appointment)}
                          >
                            Confirmar
                          </button>
                          <span className="text-gray-300">|</span>
                        </>
                      )}
                      
                      {!isCanceled && (
                        <>
                          <button 
                            type="button" 
                            className="text-red-600 hover:text-red-800 font-medium text-sm"
                            onClick={() => onCancel && onCancel(appointment)}
                          >
                            Cancelar
                          </button>
                          <span className="text-gray-300">|</span>
                          
                          <button 
                            type="button" 
                            className="text-orange-600 hover:text-orange-800 font-medium text-sm"
                            onClick={() => onReschedule && onReschedule(appointment)}
                          >
                            Remarcar
                          </button>
                          <span className="text-gray-300">|</span>
                        </>
                      )}
                      
                      <button 
                        type="button" 
                        className="text-gray-600 hover:text-gray-800 font-medium text-sm"
                        onClick={() => onDelete && onDelete(appointment)}
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AppointmentDayTable; 